import { useEffect } from 'react'

function isWakeLockSupported(): boolean {
  return typeof navigator !== 'undefined' && 'wakeLock' in navigator
}

/**
 * Holds a Screen Wake Lock while `enabled` is true, keeping the device from dimming or
 * locking the screen (e.g. during a voice call or while feedback is generating).
 *
 * Browsers auto-release the lock whenever the page loses visibility (tab hidden,
 * app backgrounded), so this re-acquires it on `visibilitychange` once the page is
 * visible again. No-op where the API is unavailable (older browsers, insecure
 * contexts); requests are wrapped defensively and never throw into the render path.
 */
export function useWakeLock(enabled: boolean): void {
  useEffect(() => {
    if (!enabled || !isWakeLockSupported()) {
      return undefined
    }

    let sentinel: WakeLockSentinel | null = null
    let cancelled = false
    let requesting = false

    const acquire = async () => {
      // Idempotent: don't request a second lock while one is still live (would orphan it).
      if (sentinel && !sentinel.released) {
        return
      }
      // ...or while a previous request is still in flight — otherwise two overlapping
      // acquires (e.g. a rapid hidden→visible flicker during mount's own request) both
      // see `sentinel === null`, both fire a request, and the loser is orphaned when the
      // last-to-resolve overwrites `sentinel` — leaving the screen awake with no way to
      // release it until the tab closes.
      if (requesting) {
        return
      }
      // The browser rejects the request unless the page is currently visible.
      if (document.visibilityState !== 'visible') {
        return
      }
      requesting = true
      try {
        const next = await navigator.wakeLock.request('screen')
        if (cancelled) {
          // Effect was torn down while awaiting — release immediately.
          void next.release()
          return
        }
        sentinel = next
      } catch {
        // Request can reject (e.g. low battery, denied permission) — degrade silently.
        sentinel = null
      } finally {
        requesting = false
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void acquire()
      }
    }

    void acquire()
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      if (sentinel) {
        void sentinel.release()
        sentinel = null
      }
    }
  }, [enabled])
}
